import React from "react";
import { Box, Fab, Typography, alpha } from "@mui/material";
import { styled } from "@mui/material/styles";
import MyLocationIcon from "@mui/icons-material/MyLocation";

// Glassmorphic map container with enhanced backdrop
export const GlassMapContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  borderRadius: 20,
  overflow: "hidden",
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.9
  )} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
  backdropFilter: "blur(30px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.15)}`,
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.primary.main,
      0.4
    )}, transparent)`,
    zIndex: 1,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 80%, ${alpha(
      theme.palette.primary.main,
      0.03
    )} 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${alpha(
      theme.palette.secondary.main,
      0.03
    )} 0%, transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },
  "& > *": {
    position: "relative",
    zIndex: 1,
  },
}));

// Enhanced glassmorphic recenter button with floating animation
export const GlassRecenterButton = styled(Fab)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  right: 20,
  transform: "translateY(-50%)",
  zIndex: 1000,
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.15
  )} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
  borderRadius: 16,
  color: theme.palette.primary.main,
  width: 56,
  height: 56,
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`,

  // Floating animation
  animation: "float 3s ease-in-out infinite",
  "@keyframes float": {
    "0%, 100%": {
      transform: "translateY(-50%) translateZ(0)",
    },
    "50%": {
      transform: "translateY(-50%) translateY(-3px) translateZ(0)",
    },
  },

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.common.white,
      0.15
    )}, transparent)`,
    opacity: 0,
    transition: "opacity 0.3s ease",
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "0",
    height: "0",
    background: `radial-gradient(circle, ${alpha(
      theme.palette.primary.main,
      0.3
    )} 0%, transparent 70%)`,
    borderRadius: "50%",
    transform: "translate(-50%, -50%)",
    transition: "all 0.3s ease",
  },

  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.25
    )} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
    transform: "translateY(-50%) translateY(-4px)",
    boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.3)}`,
    animation: "none", // Stop floating on hover

    "&::before": {
      opacity: 1,
    },

    "&::after": {
      width: "100px",
      height: "100px",
    },

    "& .MuiSvgIcon-root": {
      transform: "scale(1.1)",
    },
  },

  "&:active": {
    transform: "translateY(-50%) translateY(-1px)",
    transition: "all 0.1s ease",
  },

  "& .MuiSvgIcon-root": {
    fontSize: 28,
    transition: "transform 0.3s ease",
    filter: `drop-shadow(0 2px 4px ${alpha(theme.palette.primary.main, 0.3)})`,
  },
}));

// Glassmorphic info window with enhanced styling
export const GlassInfoWindow = styled(Box)(({ theme }) => ({
  minWidth: 250,
  maxWidth: 350,
  padding: theme.spacing(2),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.95
  )} 0%, ${alpha(theme.palette.background.paper, 0.85)} 100%)`,
  backdropFilter: "blur(25px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  borderRadius: 16,
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 15px 45px ${alpha(theme.palette.common.black, 0.2)}`,

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.primary.main,
      0.5
    )}, transparent)`,
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: `radial-gradient(circle at 10% 20%, ${alpha(
      theme.palette.primary.main,
      0.05
    )} 0%, transparent 50%)`,
    pointerEvents: "none",
  },
}));

// Glassmorphic site name button for info windows
export const GlassSiteNameButton = styled(Box)(({ theme }) => ({
  border: "none",
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.12
  )} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
  backdropFilter: "blur(10px)",
  borderRadius: 12,
  padding: theme.spacing(1.5, 2),
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "1.1rem",
  color: theme.palette.primary.main,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  textAlign: "center",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.common.white,
      0.2
    )}, transparent)`,
    transition: "left 0.6s ease",
  },

  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.18
    )} 0%, ${alpha(theme.palette.primary.main, 0.09)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
    transform: "translateY(-2px)",
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.25)}`,

    "&::before": {
      left: "100%",
    },
  },

  "&:active": {
    transform: "translateY(0px)",
    transition: "all 0.1s ease",
  },
}));

// Glassmorphic loading overlay for map
export const GlassLoadingOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.8
  )} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
  backdropFilter: "blur(15px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: theme.spacing(2),
  zIndex: 2000,
  borderRadius: 20,

  "& .MuiCircularProgress-root": {
    color: theme.palette.primary.main,
    filter: `drop-shadow(0 4px 8px ${alpha(theme.palette.primary.main, 0.3)})`,
  },
}));

// Glassmorphic location status indicator
export const GlassLocationStatus = styled(Box)(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case "loading":
        return theme.palette.warning.main;
      case "success":
        return theme.palette.success.main;
      case "error":
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const statusColor = getStatusColor();

  return {
    position: "absolute",
    top: 20,
    left: 20,
    zIndex: 1000,
    padding: theme.spacing(1, 1.5),
    background: `linear-gradient(135deg, ${alpha(
      statusColor,
      0.15
    )} 0%, ${alpha(statusColor, 0.08)} 100%)`,
    backdropFilter: "blur(15px)",
    border: `1px solid ${alpha(statusColor, 0.25)}`,
    borderRadius: 12,
    color: statusColor,
    fontSize: "0.875rem",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    transition: "all 0.3s ease",

    "&::before": {
      content: '""',
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: statusColor,
      animation: status === "loading" ? "pulse 2s infinite" : "none",
    },

    "@keyframes pulse": {
      "0%, 100%": {
        opacity: 1,
        transform: "scale(1)",
      },
      "50%": {
        opacity: 0.5,
        transform: "scale(1.2)",
      },
    },
  };
});

// Glassmorphic map controls container
export const GlassMapControls = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 20,
  right: 20,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  zIndex: 1000,
}));

// Glassmorphic map legend
export const GlassMapLegend = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 20,
  left: 20,
  padding: theme.spacing(1.5, 2),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.9
  )} 0%, ${alpha(theme.palette.background.paper, 0.75)} 100%)`,
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  borderRadius: 12,
  zIndex: 1000,
  minWidth: 200,

  "& .legend-title": {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },

  "& .legend-item": {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },

  "& .legend-icon": {
    width: 16,
    height: 10,
    borderRadius: 2,
    flexShrink: 0,
  },
}));

// Glassmorphic zoom controls
export const GlassZoomControls = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.9
  )} 0%, ${alpha(theme.palette.background.paper, 0.75)} 100%)`,
  backdropFilter: "blur(15px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  borderRadius: 12,
  overflow: "hidden",

  "& button": {
    width: 40,
    height: 40,
    border: "none",
    background: "transparent",
    color: theme.palette.primary.main,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.2rem",
    fontWeight: "bold",
    transition: "all 0.2s ease",

    "&:hover": {
      background: alpha(theme.palette.primary.main, 0.1),
    },

    "&:not(:last-child)": {
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    },
  },
}));

